import type { ReactNode } from "react";
import { useI18n } from "@wato/i18n/react";
import { Icon, cx, type IconName } from "@wato/ui";

/** OWN-223: new look — rounded-2xl cards, big KPI numbers with a tinted icon tile, pill toggle. */

/** One KPI: label, big number and an optional small hint line. Rendered inside a `<dl>`. */
export function KpiCard({
  label,
  value,
  hint,
  valueTestId,
  title,
  extra,
  compact,
  icon = "chart",
}: {
  readonly label: string;
  readonly value: string;
  readonly hint?: string | undefined;
  readonly valueTestId?: string | undefined;
  readonly title?: string | undefined;
  readonly extra?: ReactNode;
  readonly compact?: boolean | undefined;
  readonly icon?: IconName;
}) {
  return (
    <div
      className="flex min-w-0 items-center gap-4 rounded-2xl border border-line bg-surface p-5"
      title={title}
    >
      <span
        aria-hidden="true"
        className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-app-accent text-app-on-accent"
      >
        <Icon name={icon} size={24} />
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <dd
          className={cx(
            "order-2 font-bold tabular-nums text-fg [overflow-wrap:anywhere]",
            compact ? "text-2xl sm:text-3xl" : "text-4xl",
          )}
          data-testid={valueTestId}
        >
          {value}
        </dd>
        <dt className="order-1 truncate text-sm text-fg-muted">{label}</dt>
        {hint ? <dd className="order-3 text-xs text-fg-muted">{hint}</dd> : null}
        {extra ? <dd className="order-4 text-xs">{extra}</dd> : null}
      </div>
    </div>
  );
}

/** Consistent titled container for a chart or table. */
export function ChartCard({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-line bg-surface p-5">
      <h2 className="text-lg font-bold text-fg">{title}</h2>
      {children}
    </section>
  );
}

/** Friendly "no data yet" block: icon tile + one short line. */
export function EmptyData({ hint }: { readonly hint?: string | undefined }) {
  const { t } = useI18n();
  return (
    <div
      data-testid="analytics-empty"
      className="flex flex-col items-center gap-3 rounded-2xl bg-surface-sunken px-4 py-8 text-center"
    >
      <span
        aria-hidden="true"
        className="flex size-14 items-center justify-center rounded-2xl bg-surface text-fg-muted"
      >
        <Icon name="chart" size={24} />
      </span>
      <p className="font-semibold text-fg">{t("analytics.empty.title")}</p>
      <p className="max-w-md text-sm text-fg-muted">{hint ?? t("analytics.empty.hint")}</p>
    </div>
  );
}

/** Pulsing placeholder tiles shown while a report loads. */
export function KpiSkeleton({ count = 8 }: { readonly count?: number }) {
  const { t } = useI18n();
  return (
    <div role="status" aria-busy="true" className="flex flex-col gap-5">
      <span className="sr-only">{t("dashboard.shell.loading")}</span>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: count }, (_, i) => (
          <div
            key={i}
            className="flex animate-pulse items-center gap-4 rounded-2xl border border-line bg-surface p-5"
          >
            <span className="block size-14 rounded-2xl bg-surface-muted" />
            <span className="flex flex-1 flex-col gap-2">
              <span className="block h-3 w-1/2 rounded-md bg-surface-muted" />
              <span className="block h-8 w-2/3 rounded-md bg-surface-muted" />
            </span>
          </div>
        ))}
      </div>
      <div className="h-40 animate-pulse rounded-2xl border border-line bg-surface" />
    </div>
  );
}

/** Pill window switch (7/30 days etc.) built on `aria-pressed` buttons. */
export function WindowToggle<T extends string | number>({
  label,
  options,
  value,
  onChange,
}: {
  readonly label: string;
  readonly options: readonly { readonly value: T; readonly label: string }[];
  readonly value: T;
  readonly onChange: (v: T) => void;
}) {
  return (
    <div role="group" aria-label={label} className="inline-flex w-fit flex-wrap gap-1.5">
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className="inline-flex h-9 items-center rounded-full px-4 text-sm text-fg-muted hover:bg-surface-sunken aria-pressed:bg-action-primary aria-pressed:font-semibold aria-pressed:text-on-action-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-action-primary"
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export const ANALYTICS_TABLE = "w-full text-sm text-fg tabular-nums";
export const ANALYTICS_TH =
  "border-b border-line px-3 py-2 text-start text-xs font-medium text-fg-muted";
export const ANALYTICS_TD = "border-b border-line px-3 py-2 text-start";

/** Thin proportional bar for a 0–100 share. */
export function ShareBar({ percent, label }: { readonly percent: number; readonly label: string }) {
  const p = Math.max(0, Math.min(100, percent));
  return (
    <div
      role="img"
      aria-label={label}
      className="h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken"
    >
      <div className="h-full bg-action-primary" style={{ inlineSize: `${p}%` }} />
    </div>
  );
}
